import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FAQ } from '../utils/types';

const faqs: FAQ[] = [
  {
    question: 'What is a trading bot?',
    answer: 'A trading bot is an automated software that executes buy and sell orders on cryptocurrency exchanges based on predefined rules and strategies. It can trade 24/7 without human intervention, potentially capitalizing on market opportunities that might be missed due to time constraints or emotional decision-making.'
  },
  {
    question: 'Do I need programming knowledge to use BOTINO.NET?',
    answer: 'No, BOTINO.NET is designed to be user-friendly for traders of all technical backgrounds. Our intuitive interface allows you to create, customize, and deploy trading bots without writing a single line of code. We handle all the technical aspects so you can focus on your trading strategy.'
  },
  {
    question: 'How do I integrate TradingView with BOTINO.NET?',
    answer: 'You can easily connect TradingView alerts to BOTINO.NET through our webhook integration system. Simply create alerts in TradingView and set the webhook URL provided in your BOTINO.NET dashboard. Our system will then translate these alerts into trading actions according to your bot configuration.'
  },
  {
    question: 'How secure is BOTINO.NET?',
    answer: 'Security is our top priority. We use bank-grade encryption for all sensitive data and API keys. Your exchange API keys are stored with end-to-end encryption, and we never have withdrawal access to your funds. We also employ regular security audits, two-factor authentication, and follow industry best practices for data protection.'
  },
  {
    question: 'Which exchanges does BOTINO.NET support?',
    answer: 'BOTINO.NET supports all major cryptocurrency exchanges including Binance, Coinbase Pro, Kraken, Bitfinex, and many more. We continuously add support for additional exchanges based on user demand and market relevance.'
  },
  {
    question: 'Can I test my strategy before risking real money?',
    answer: 'Yes, BOTINO.NET offers backtesting and paper trading features that allow you to test your trading strategies using historical data or in real-time market conditions without risking actual funds. This helps you validate and refine your strategies before deploying them with real capital.'
  },
];

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            Frequently Asked <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">Questions</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Find answers to common questions about BOTINO.NET and our crypto trading bot platform.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="mb-4"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full flex justify-between items-center p-6 rounded-lg text-left transition-colors ${
                  openIndex === index 
                    ? 'bg-gray-100 dark:bg-gray-800' 
                    : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{faq.question}</h3>
                {openIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                )}
              </button>
              
              {openIndex === index && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-50 dark:bg-gray-800 p-6 rounded-b-lg border-t-0 border border-gray-200 dark:border-gray-700"
                >
                  <p className="text-gray-600 dark:text-gray-300">{faq.answer}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};